import { useState } from 'react';
import { Percent, Loader2, Check, X } from 'lucide-react';
import * as api from '../../lib/api';
import { formatCents } from '../../lib/format';

// A typical monthly rent here, used to make an abstract rate concrete.
const EXAMPLE_PAYMENT_CENTS = 18950;

/**
 * Setting the commission.
 *
 * Entered as a percentage because that is how the deal is discussed, stored as
 * basis points because that is how it has to be applied — a float rate across
 * ten thousand transactions drifts. The conversion happens once, here.
 *
 * The worked example is not decoration. "2.5%" is abstract; "$4.74 on a
 * $189.50 payment" is the number someone can check against what they meant to
 * charge, before thousands of transactions are priced by it.
 */
export default function CommissionSettings({ settings, onSaved }) {
  const [open, setOpen] = useState(false);
  const [percent, setPercent] = useState((settings.commission_bps / 100).toFixed(2));
  const [terms, setTerms] = useState(String(settings.payment_terms_days ?? 14));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const parsed = Number.parseFloat(percent);
  const bps = Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  const valid = bps !== null && bps >= 0 && bps <= 10000;
  const changed = bps !== settings.commission_bps || Number(terms) !== settings.payment_terms_days;

  const save = async (event) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      const { settings: next } = await api.updatePlatformSettings({
        commission_bps: bps,
        payment_terms_days: Number(terms),
      });
      onSaved(next);
      setOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <Percent className="h-5 w-5 text-brand-600" aria-hidden="true" />
        <p className="mt-3 text-2xl font-extrabold tabular-nums text-slate-900">
          {(settings.commission_bps / 100).toFixed(2)}%
        </p>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Commission</p>
        <button type="button" onClick={() => setOpen(true)}
                className="mt-1 text-[12px] font-bold text-brand-700 hover:underline">
          Change rate
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={save}
          className="rounded-xl border border-brand-300 bg-white p-4 ring-2 ring-brand-500/20 sm:col-span-2">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Commission</p>
        <button type="button" onClick={() => setOpen(false)} aria-label="Cancel"
                className="rounded p-0.5 text-slate-400 hover:bg-slate-100">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {error && <p role="alert" className="mt-2 rounded-lg bg-rose-50 p-2 text-xs text-rose-800">{error}</p>}

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <label className="field-label mb-1" htmlFor="rate">Rate</label>
          <div className="relative">
            <input id="rate" type="number" min={0} max={100} step="0.01" required autoFocus
                   className="field-input pr-8" value={percent}
                   onChange={(e) => setPercent(e.target.value)}
                   aria-invalid={!valid || undefined} />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">%</span>
          </div>
        </div>
        <div>
          <label className="field-label mb-1" htmlFor="terms">Payment terms</label>
          <div className="relative">
            <input id="terms" type="number" min={0} max={90} required
                   className="field-input pr-14" value={terms}
                   onChange={(e) => setTerms(e.target.value)} />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">days</span>
          </div>
        </div>
      </div>

      {valid ? (
        <p className="mt-2 text-xs text-slate-600">
          On a {formatCents(EXAMPLE_PAYMENT_CENTS)} payment you take{' '}
          <strong className="font-bold text-slate-900">
            {formatCents(Math.round((EXAMPLE_PAYMENT_CENTS * bps) / 10000))}
          </strong>.
        </p>
      ) : (
        <p className="mt-2 text-xs text-rose-700">Enter a rate between 0 and 100.</p>
      )}

      <p className="mt-2 text-[12px] text-slate-500">
        Applies to payments from now on. Statements already issued keep the rate they were
        charged at.
      </p>

      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={busy || !valid || !changed}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-bold text-white hover:bg-brand-700 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <Check className="h-4 w-4" aria-hidden="true" />}
          Save
        </button>
        <button type="button" onClick={() => setOpen(false)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
      </div>
    </form>
  );
}
